import {addQueryToPath} from '../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {setRecentSearchQueries} from '../../../../../srcCommon/services/storage/recentSearchQueries';
import {CancelError, CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {ActionBase, ActionWithPayload} from '../../../../helpers/reduxHelpers';
import {ApiSearchHints, SearchHintModel} from '../../../../models/api/searchHintModel';
import {
  ApiSearchSuggestionsMatches,
  SearchSuggestionMatchModel,
} from '../../../../models/api/searchSuggestionMatchModel';
import {InboxesSearchLocationsEnum} from '../../../../models/url/inboxesUrlModel';
import {fetchApiAsync} from '../../../../services/http/http';
import {HttpError} from '../../../../services/http/httpError';
import {CallApiFailedAction} from '../../../api/apiActions';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {getCompany} from '../../../data/company/companySelectors';
import {
  InboxesConversationsParametersWithSearch,
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../inboxes/parameters/inboxesParameters';
import {
  buildParametersFetchQueryContext,
  buildParametersSearchHintsFetchPath,
  buildParametersSearchSuggestionsFetchPath,
} from '../../inboxes/parameters/inboxesParametersFetchHelpers';
import {CellsContext} from '../context/cellsContext';
import {makeCellsContext} from '../context/cellsContextHelpers';
import {buildSearchParameters} from '../helpers/cellsPanelPathHelpers';
import {getCellsSearchRecentQueries, getCellsSearchSessionTimestamp} from './cellsPanelSearchSelectors';
import {HighlightAlgorithmsEnum} from './cellsPanelSearchState';

/*
 * Constants.
 */

const RECENT_QUERIES_MAX_LENGTH = 60;
export const SEARCH_SESSION_DURATION_MS = 10 * 60 * 1000; // 10 minutes.

/*
 * Type enum.
 */

export enum CellsPanelSearchActionsEnum {
  SET_RESULTS_COUNT = '@@panels/cells/SET_RESULTS_COUNT',
  RESET_RESULTS_COUNTS = '@@panels/cells/RESET_RESULTS_COUNTS',
  SAVE_RECENT_QUERIES = '@@panels/cells/SAVE_RECENT_QUERIES',
  CLEAR_SUGGESTIONS = '@@panels/cells/CLEAR_SUGGESTIONS',
  CLEAR_SEARCH_HINTS = '@@panels/cells/CLEAR_SEARCH_HINTS',
  SET_HIGHLIGHT_ALGORITHM = '@@panels/cells/SET_HIGHLIGHT_ALGORITHM',
}

export enum CellsPanelSearchSuggestionsActionsEnum {
  FETCH_SUGGESTIONS = '@@panels/cells/FETCH_SUGGESTIONS',
  FETCH_SUGGESTIONS_COMPLETED = '@@panels/cells/FETCH_SUGGESTIONS_COMPLETED',
  FETCH_SUGGESTIONS_FAILED = '@@panels/cells/FETCH_SUGGESTIONS_FAILED',
}

export enum CellsPanelSearchHintsActionsEnum {
  FETCH_SEARCH_HINTS = '@@panels/cells/FETCH_SEARCH_HINTS',
  FETCH_SEARCH_HINTS_COMPLETED = '@@panels/cells/FETCH_SEARCH_HINTS_COMPLETED',
  FETCH_SEARCH_HINTS_FAILED = '@@panels/cells/FETCH_SEARCH_HINTS_FAILED',
}

export enum CellsPanelSearchSessionTimestampActionsEnum {
  UPDATE_SEARCH_SESSION_TIMESTAMP = '@@panels/cells/UPDATE_SEARCH_SESSION_TIMESTAMP',
}

/*
 * Types.
 */

interface SearchSearchResultsCountActionPayload {
  location: InboxesSearchLocationsEnum;
  count: number;
}

interface SetSearchResultsCountAction extends ActionWithPayload<SearchSearchResultsCountActionPayload> {
  type: CellsPanelSearchActionsEnum.SET_RESULTS_COUNT;
}

interface ResetSearchResultsCountsAction extends ActionBase {
  type: CellsPanelSearchActionsEnum.RESET_RESULTS_COUNTS;
}

interface SaveRecentQueriesAction extends ActionWithPayload<Array<string>> {
  type: CellsPanelSearchActionsEnum.SAVE_RECENT_QUERIES;
}

interface ClearSearchSuggestionsAction extends ActionBase {
  type: CellsPanelSearchActionsEnum.CLEAR_SUGGESTIONS;
}

interface ClearSearchHintsAction extends ActionBase {
  type: CellsPanelSearchActionsEnum.CLEAR_SEARCH_HINTS;
}

interface FetchSearchSuggestionsPayload {
  query: string;
}

interface FetchSearchSuggestionsCompletedPayload {
  results: ReadonlyArray<SearchSuggestionMatchModel>;
}

interface FetchSearchSuggestionsAction extends ActionWithPayload<FetchSearchSuggestionsPayload> {
  type: CellsPanelSearchSuggestionsActionsEnum.FETCH_SUGGESTIONS;
}

interface FetchSearchSuggestionsCompletedAction
  extends ActionWithPayload<FetchSearchSuggestionsCompletedPayload> {
  type: CellsPanelSearchSuggestionsActionsEnum.FETCH_SUGGESTIONS_COMPLETED;
}
interface FetchSearchSuggestionsFailedAction extends CallApiFailedAction<FetchSearchSuggestionsPayload> {
  type: CellsPanelSearchSuggestionsActionsEnum.FETCH_SUGGESTIONS_FAILED;
}

interface FetchSearchHintsCompletedPayload {
  hints: ReadonlyArray<SearchHintModel>;
}

interface FetchSearchHintsAction extends ActionBase {
  type: CellsPanelSearchHintsActionsEnum.FETCH_SEARCH_HINTS;
}

interface FetchSearchHintsCompletedAction extends ActionWithPayload<FetchSearchHintsCompletedPayload> {
  type: CellsPanelSearchHintsActionsEnum.FETCH_SEARCH_HINTS_COMPLETED;
}

interface FetchSearchHintsFailedAction extends CallApiFailedAction<{}> {
  type: CellsPanelSearchHintsActionsEnum.FETCH_SEARCH_HINTS_FAILED;
}

interface UpdateSearchSessionTimestampPayload {
  timestamp: number;
}

interface UpdateSearchSessionTimestampAction extends ActionWithPayload<UpdateSearchSessionTimestampPayload> {
  type: CellsPanelSearchSessionTimestampActionsEnum.UPDATE_SEARCH_SESSION_TIMESTAMP;
}

interface SetHighlightAlgorithmAction extends ActionWithPayload<HighlightAlgorithmsEnum> {
  type: CellsPanelSearchActionsEnum.SET_HIGHLIGHT_ALGORITHM;
}

/*
 * Search results counts action creators.
 */

export function setSearchResultsCountAction(
  location: InboxesSearchLocationsEnum,
  count: number,
): SetSearchResultsCountAction {
  return {
    type: CellsPanelSearchActionsEnum.SET_RESULTS_COUNT,
    payload: {
      location,
      count,
    },
  };
}

export function resetSearchResultsCountsAction(): ResetSearchResultsCountsAction {
  return {
    type: CellsPanelSearchActionsEnum.RESET_RESULTS_COUNTS,
  };
}

/*
 * Save recent query action creator.
 */

export function saveRecentQueryAction(query?: string): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // If query is not defined, return early.
    if (!query) {
      return;
    }

    const state = getState();

    const recentQueries = getCellsSearchRecentQueries(state) || [];

    // Clone the array and remove `query` if it exists.
    const newRecentQueries = [...recentQueries.filter((recentQuery) => recentQuery !== query)];
    newRecentQueries.unshift(query);

    // If there are too many queries, remove the oldest.
    if (newRecentQueries.length > RECENT_QUERIES_MAX_LENGTH) {
      newRecentQueries.pop();
    }

    // Save recent queries to localStorage.
    setRecentSearchQueries(newRecentQueries);

    dispatch<SaveRecentQueriesAction>({
      type: CellsPanelSearchActionsEnum.SAVE_RECENT_QUERIES,
      payload: newRecentQueries,
    });
  };
}

/*
 * Fetch search suggestions action creators.
 */

export function fetchSearchSuggestionsAsyncAction(
  parameters: InboxesParameters,
  selectedSearchFilter: InboxesSearchLocationsEnum,
  query?: string,
  cancelToken?: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // If query is not defined, return early.
    if (!query || parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
      return;
    }

    const state = getState();

    const cellsContext = makeCellsContext(state, parameters);
    const searchParameters = {
      location: selectedSearchFilter,
      query,
    };
    const parametersWithSearchParameters = {...parameters, search: searchParameters};
    const searchPath = buildParametersSearchSuggestionsFetchPath(parameters, searchParameters, cellsContext);
    const searchPathAndQuery = addQueryToPath(searchPath, {
      context: buildParametersFetchQueryContext(parametersWithSearchParameters, cellsContext),
      filters: parameters.filters.join(','),
      ...buildSearchParameters(state, parametersWithSearchParameters),
    });

    const company = getCompany(state);
    const path = `${company.url}${searchPathAndQuery}`;

    dispatch<FetchSearchSuggestionsAction>({
      type: CellsPanelSearchSuggestionsActionsEnum.FETCH_SUGGESTIONS,
      payload: {query},
    });

    try {
      // Initiate a fetch from the network.
      const fetchSearchSuggestionsPromise = fetchApiAsync(ApiSearchSuggestionsMatches, path, {cancelToken});

      const searchSuggestions = await fetchSearchSuggestionsPromise;

      dispatch<FetchSearchSuggestionsCompletedAction>({
        type: CellsPanelSearchSuggestionsActionsEnum.FETCH_SUGGESTIONS_COMPLETED,
        payload: {
          ...searchSuggestions,
        },
      });
    } catch (error) {
      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchSearchSuggestionsFailedAction>({
        type: CellsPanelSearchSuggestionsActionsEnum.FETCH_SUGGESTIONS_FAILED,
        payload: {
          query,
          error,
        },
      });
    }
  };
}

export function clearSearchSuggestionsAction(): ClearSearchSuggestionsAction {
  return {
    type: CellsPanelSearchActionsEnum.CLEAR_SUGGESTIONS,
  };
}

/*
 * Fetch search hints action creators.
 */

export function fetchSearchHintsAsyncAction(
  parameters: InboxesConversationsParametersWithSearch,
  cellsContext: CellsContext,
  cancelToken?: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();

    const searchPath = buildParametersSearchHintsFetchPath(parameters, parameters.search, cellsContext);
    const searchPathAndQuery = addQueryToPath(searchPath, {
      context: buildParametersFetchQueryContext(parameters, cellsContext),
      filters: parameters.filters.join(','),
      ...buildSearchParameters(state, parameters),
    });

    const company = getCompany(state);
    const path = `${company.url}${searchPathAndQuery}`;

    dispatch<FetchSearchHintsAction>({
      type: CellsPanelSearchHintsActionsEnum.FETCH_SEARCH_HINTS,
    });

    try {
      // Initiate a fetch from the network.
      const fetchSearchHintsPromise = fetchApiAsync(ApiSearchHints, path, {cancelToken});

      const hints = await fetchSearchHintsPromise;

      dispatch<FetchSearchHintsCompletedAction>({
        type: CellsPanelSearchHintsActionsEnum.FETCH_SEARCH_HINTS_COMPLETED,
        payload: {
          hints,
        },
      });
    } catch (error) {
      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchSearchHintsFailedAction>({
        type: CellsPanelSearchHintsActionsEnum.FETCH_SEARCH_HINTS_FAILED,
        payload: {
          error,
        },
      });
    }
  };
}

export function clearSearchHintsAction(): ClearSearchHintsAction {
  return {
    type: CellsPanelSearchActionsEnum.CLEAR_SEARCH_HINTS,
  };
}

/*
 * Search session action creators.
 */

export function maybeUpdateSearchSessionTimestampAction(): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const searchSessionTimestamp = getCellsSearchSessionTimestamp(state);

    const now = Date.now();

    // If searchSessionTimestamp is defined and we did not crossed the session duration, return early.
    if (searchSessionTimestamp && searchSessionTimestamp + SEARCH_SESSION_DURATION_MS > now) {
      return;
    }

    dispatch({
      type: CellsPanelSearchSessionTimestampActionsEnum.UPDATE_SEARCH_SESSION_TIMESTAMP,
      payload: {
        timestamp: now,
      },
    });
  };
}

/*
 * Highlight algorithms.
 */

export function setHighlightAlgorithmAction(algorithm: HighlightAlgorithmsEnum): SetHighlightAlgorithmAction {
  return {
    type: CellsPanelSearchActionsEnum.SET_HIGHLIGHT_ALGORITHM,
    payload: algorithm,
  };
}

/*
 * Combined action type.
 */

type CellsPanelFetchSearchSuggestionsAction =
  | FetchSearchSuggestionsAction
  | FetchSearchSuggestionsCompletedAction
  | FetchSearchSuggestionsFailedAction;

type CellsPanelFetchSearchHintsAction =
  | FetchSearchHintsAction
  | FetchSearchHintsCompletedAction
  | FetchSearchHintsFailedAction;

export type CellsPanelSearchAction =
  | SetSearchResultsCountAction
  | ResetSearchResultsCountsAction
  | SaveRecentQueriesAction
  | ClearSearchSuggestionsAction
  | ClearSearchHintsAction
  | UpdateSearchSessionTimestampAction
  | CellsPanelFetchSearchSuggestionsAction
  | CellsPanelFetchSearchHintsAction
  | SetHighlightAlgorithmAction;
